/**
 * Service for uploading floor plan images and processing them
 * through the CV extraction pipeline.
 */

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, storage } from '../firebase';
import { createCallable } from '../firebaseFunctions';

const EXTRACTOR_URL =
  process.env.REACT_APP_FLOORPLAN_EXTRACTOR_URL ||
  `${process.env.REACT_APP_FUNCTIONS_URL}/floorplanExtraction`;

// The DXF path is a different EXTRACTOR, not a different pipeline: it
// returns the same cvOutput, and everything downstream — the structure
// builder, the save, the extraction editor — is unchanged.
const DXF_EXTRACTOR_URL =
  process.env.REACT_APP_DXF_EXTRACTOR_URL ||
  `${process.env.REACT_APP_FUNCTIONS_URL}/extractDxf`;

// Below this, the plans share too little wall length for the alignment
// to mean anything, and stacking them would invent a building.
const MIN_STACK_REGISTRATION = 0.25;

/** A vector drawing routes to the DXF extractor; anything else is an image. */
export const isDxfFile = (file) => /\.dxf$/i.test(file?.name || '');

/**
 * Upload a floor plan image and extract structure.
 *
 * @param {File} imageFile - The image file to process
 * @param {string} projectId - Project to create the structure in
 * @param {function} onProgress - Progress callback: (stage, message)
 * @returns {Promise<{structureId, roomIds}>}
 */
export async function uploadAndExtractFloorplan(
  imageFile,
  projectId,
  onProgress = () => {},
  options = {}
) {
  // One entry point, two extractors — the caller just hands over a file.
  if (isDxfFile(imageFile)) {
    return uploadAndExtractDxf(imageFile, projectId, onProgress, options);
  }

  // Step 1: Upload image to Firebase Storage
  onProgress('upload', 'Uploading image...');
  const timestamp = Date.now();
  const filename = `${timestamp}_${imageFile.name}`;
  const storageRef = ref(
    storage,
    `projects/${projectId}/floorplan-uploads/${filename}`
  );
  await uploadBytes(storageRef, imageFile);
  const downloadUrl = await getDownloadURL(storageRef);
  const gsUrl = `gs://${storageRef.bucket}/${storageRef.fullPath}`;

  // Step 2: Call Python CV Cloud Function
  onProgress('extract', 'Running CV extraction pipeline...');
  if (!auth.currentUser) {
    throw new Error('Must be signed in to upload a floor plan.');
  }
  const idToken = await auth.currentUser.getIdToken();
  const cvResponse = await fetch(EXTRACTOR_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ imageUrl: downloadUrl }),
  });

  if (!cvResponse.ok) {
    const text = await cvResponse.text();
    throw new Error(`CV extraction failed: ${cvResponse.status} ${text}`);
  }

  const cvOutput = await cvResponse.json();

  // Step 3: Call Node.js Cloud Function to build structure and save
  onProgress('build', 'Building structure...');
  const processFloorplan = createCallable('processFloorplanUpload');
  const result = await processFloorplan({
    cvOutput,
    projectId,
    floorplanImageUrl: gsUrl,
    imageDownloadUrl: downloadUrl,
  });

  onProgress('done', 'Structure created');
  return result.data;
}

/**
 * Upload a DXF drawing and extract structure.
 *
 * Two round trips, because model space holds a whole sheet set (plans,
 * elevations, sections, often a survey) and the caller has to say which
 * plan it wants. The first call lists the plans found; the second
 * extracts one.
 *
 * @param {File} dxfFile
 * @param {string} projectId
 * @param {function} onProgress - (stage, message)
 * @param {Object} [options]
 * @param {function} [options.onChoosePlan] - (plans) => index. Defaults to
 *   the richest plan found, reported through onProgress.
 * @returns {Promise<{structureId, roomIds}>}
 */
export async function uploadAndExtractDxf(
  dxfFile,
  projectId,
  onProgress = () => {},
  { onChoosePlan } = {}
) {
  if (!auth.currentUser) {
    throw new Error('Must be signed in to upload a floor plan.');
  }

  onProgress('upload', 'Uploading drawing...');
  const dxfRef = ref(
    storage,
    `projects/${projectId}/floorplan-uploads/${Date.now()}_${dxfFile.name}`
  );
  await uploadBytes(dxfRef, dxfFile);
  const dxfUrl = await getDownloadURL(dxfRef);

  const call = async (body, stage, message) => {
    onProgress(stage, message);
    const idToken = await auth.currentUser.getIdToken();
    const res = await fetch(DXF_EXTRACTOR_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ dxfUrl, ...body }),
    });
    if (!res.ok) {
      throw new Error(
        `DXF extraction failed: ${res.status} ${await res.text()}`
      );
    }
    return res.json();
  };

  const { plans } = await call({}, 'plans', 'Finding floor plans...');
  if (!plans?.length) {
    throw new Error('No floor plans found in this drawing.');
  }

  let cvOutput;
  let planPng;

  if (plans.length === 1) {
    ({ cvOutput, planPng } = await call(
      { window: plans[0].window },
      'extract',
      'Extracting floor plan...'
    ));
  } else {
    // Every storey, in one structure. Each plan is registered onto the
    // first so the storeys stack the way the BUILDING does rather than
    // the way the sheet was tiled.
    const stacked = await call(
      { stack: true },
      'extract',
      `Extracting ${plans.length} storeys...`
    );
    const weakest = Math.min(
      ...stacked.stories.map((st) => st.registration ?? 0)
    );

    if (weakest >= MIN_STACK_REGISTRATION) {
      ({ cvOutput, planPng } = stacked);
    } else {
      // The plans don't line up, so they may not be storeys of one
      // building at all — an alternate scheme, a different structure, a
      // plan at another scale. Ask instead of stacking something wrong.
      if (!onChoosePlan) {
        throw new Error(
          `These ${plans.length} floor plans do not line up — choose one to import.`
        );
      }
      const index = await onChoosePlan(plans);
      if (index == null || index < 0 || index >= plans.length) {
        throw new Error('No floor plan selected.');
      }
      ({ cvOutput, planPng } = await call(
        { window: plans[index].window },
        'extract',
        'Extracting floor plan...'
      ));
    }
  }

  // The extraction editor draws its overlay on the source image. A DXF
  // has none, so the extractor rasterizes the plan in the cvOutput's
  // exact frame and we store that in its place.
  onProgress('upload', 'Storing plan image...');
  const pngRef = ref(
    storage,
    `projects/${projectId}/floorplan-uploads/${Date.now()}_${dxfFile.name}.png`
  );
  await uploadBytes(pngRef, base64ToBlob(planPng, 'image/png'), {
    contentType: 'image/png',
  });
  const pngDownloadUrl = await getDownloadURL(pngRef);

  onProgress('build', 'Building structure...');
  const processFloorplan = createCallable('processFloorplanUpload');
  const result = await processFloorplan({
    cvOutput,
    projectId,
    floorplanImageUrl: `gs://${pngRef.bucket}/${pngRef.fullPath}`,
    imageDownloadUrl: pngDownloadUrl,
  });

  onProgress('done', 'Structure created');
  return result.data;
}

function base64ToBlob(b64, contentType) {
  const bytes = atob(b64);
  const buf = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
  return new Blob([buf], { type: contentType });
}

/**
 * Upload a NON-ANNOTATED + ANNOTATED image pair and extract structure.
 *
 * Geometry is extracted from the non-annotated image; scale is calibrated
 * from the annotated image's dimension rulers (CV pair mode). The annotated
 * image is registered onto the non-annotated one so its scale lands in the
 * geometry image's pixel space. Falls back to the door-width scale when no
 * confident dimension annotations are found.
 *
 * @param {File} nonAnnotatedFile - clean floor plan (geometry source)
 * @param {File} annotatedFile - dimensioned floor plan (scale source)
 * @param {string} projectId - Project to create the structure in
 * @param {function} onProgress - Progress callback: (stage, message)
 * @returns {Promise<{structureId, roomIds, dimensions}>}
 */
export async function uploadAndExtractFloorplanPair(
  nonAnnotatedFile,
  annotatedFile,
  projectId,
  onProgress = () => {}
) {
  if (!auth.currentUser) {
    throw new Error('Must be signed in to upload a floor plan.');
  }

  // Step 1: Upload both images to Firebase Storage
  onProgress('upload', 'Uploading image pair...');
  const upload = async (file, prefix) => {
    const storageRef = ref(
      storage,
      `projects/${projectId}/floorplan-uploads/${prefix}_${Date.now()}_${file.name}`
    );
    await uploadBytes(storageRef, file);
    return {
      gsUrl: `gs://${storageRef.bucket}/${storageRef.fullPath}`,
      downloadUrl: await getDownloadURL(storageRef),
    };
  };
  const geom = await upload(nonAnnotatedFile, 'plan');
  const annotated = await upload(annotatedFile, 'annotated');

  // Step 2: Call Python CV Cloud Function in pair mode
  onProgress('extract', 'Running CV extraction (with dimensions)...');
  const idToken = await auth.currentUser.getIdToken();
  const cvResponse = await fetch(EXTRACTOR_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      imageUrl: geom.downloadUrl,
      dimensionImageUrl: annotated.downloadUrl,
    }),
  });

  if (!cvResponse.ok) {
    const text = await cvResponse.text();
    throw new Error(`CV extraction failed: ${cvResponse.status} ${text}`);
  }

  const cvOutput = await cvResponse.json();

  // Step 3: Build structure and save (creates a new structure)
  onProgress('build', 'Building structure...');
  const processFloorplan = createCallable('processFloorplanUpload');
  const result = await processFloorplan({
    cvOutput,
    projectId,
    floorplanImageUrl: geom.gsUrl,
    imageDownloadUrl: geom.downloadUrl,
  });

  onProgress('done', 'Structure created');
  return { ...result.data, dimensions: cvOutput.dimensions || null };
}

/**
 * Load a cv-output.json file and process it into a structure
 * (without uploading an image to the CV function — for testing).
 *
 * @param {Object} cvOutput - Parsed cv-output.json
 * @param {string} projectId - Project to create the structure in
 * @param {string} imageDownloadUrl - Download URL for the floor plan image
 * @returns {Promise<{structureId, roomIds}>}
 */
export async function processLocalCvOutput(
  cvOutput,
  projectId,
  imageDownloadUrl
) {
  const processFloorplan = createCallable('processFloorplanUpload');
  const result = await processFloorplan({
    cvOutput,
    projectId,
    imageDownloadUrl,
  });
  return result.data;
}
